import lodash from 'lodash';

// For an item that has a MAKEAMEAL/nested choiceset (e.g. "Add 2 Cans"),
// `nestedItemInValid` is computed from `purchased`, not from live Redux state,
// because flat choicesets (e.g. "Salad") update Redux directly via
// adjustOpenPurchaseChoice and never call setPurchased. Left unsynced, `purchased`
// keeps the item's original pre-selected choice forever, which
// choiceSetsWithQuantities then double-counts alongside the newly selected choice
// (the old one via the stale `purchased` fallback, the new one via its own live
// quantity), pushing the choiceset's quantity over its max and incorrectly
// invalidating it.
//
// This keeps `purchased`'s entries for FLAT (non-nested) choicesets in sync with
// the live Redux selection state, while leaving any choiceset with
// `nestedIngredients` (MAKEAMEAL/nested sub-flows) untouched — those are staged
// separately via `dataForAdjust`/`handleAddToItem` and must not be overwritten here.
//
// Returns the same `purchased` reference if nothing changed, so callers can use
// this safely as a no-op-when-unchanged React state updater.
export default function syncFlatChoiceSelectionsIntoPurchased(
  purchased: NestedChoiceSelections,
  choiceSets: (ValidatedChoiceSet | ValidatedNestedChoiceSet)[] | undefined,
  liveChoiceSelections: NestedChoiceSelections | SDict<string[]> | undefined,
): NestedChoiceSelections {
  if (!purchased || !choiceSets) return purchased;

  let changed = false;
  const next = { ...purchased };

  choiceSets.forEach(set => {
    if ('nestedIngredients' in set) return;

    const liveIds = (liveChoiceSelections as SDict<string[]> | undefined)?.[
      set.key
    ];
    if (!Array.isArray(liveIds)) return;

    const prevIds = ((purchased[set.key] as ChoiceWithQuantity[]) || []).map(
      (c: any) => (typeof c === 'string' ? c : c.id),
    );

    if (!lodash.isEqual(prevIds, liveIds)) {
      next[set.key] = liveIds
        .map(id => set.choices.find((c: any) => c.id === id))
        .filter(Boolean) as ChoiceWithQuantity[];
      changed = true;
    }
  });

  return changed ? next : purchased;
}
